import { LargeStepWrapper, LoadingFox, MuiAlert, Typography, processArrayInChunks, useIsMounted } from '@fox/atoms';
import { FlexColumnCentered, FlexColumnSpaced } from '@fox/layout';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { DO_NOT_IMPORT } from './data-grid';
import { ImportState, MapOption, ValidationResult, useImportWizardContext } from './import-wizard';

const reverseMapping = (o: Record<string, string>): Record<string, string> =>
	Object.entries(o).reduce(
		(r, [k, v]) =>
			// Object.assign(r, { [v]: (r[v] || []).concat(k) }), {})
			Object.assign(r, { [v]: k }),
		{}
	);

/** Function generator to perform a map for a column, including validating the options are valid/expected. This should be used at the start of the importer */
export const mapValue = (map: Record<string, string>, data: Record<string, string>, availableOptions?: readonly MapOption[]) => (
	key: string
) => {
	const mapped = map[key] ? data[map[key]] : null;
	if (!mapped && availableOptions && !availableOptions.some(a => a.key === key)) {
		console.error('Mapping column was not found', key, map, data);
	}
	return mapped;
};

export function Review<TImport extends object>() {
	const {
		performValidateChunk,
		state,
		mapRef,
		format,
		ReviewComponent,
		dispatch,
		chunkSize = 50,
		mapTypes,
		matchOptions,

		profile,
		importState,
		setImportState
	} = useImportWizardContext<TImport>();
	const [percent, setPercent] = useState(0);
	const mappedColumns = [
		...new Set([
			...Object.entries(mapRef)
				.map(([_, value]) => (value !== DO_NOT_IMPORT ? value : null))
				.filter(Boolean)
		])
	];

	const algo = state.matchAlgorithm;

	const queryKey = ['import', state.fileData, state.sheet, state.customMatchKeys, profile, algo] as const;

	const mounted = useIsMounted();

	useQuery({
		refetchOnWindowFocus: false,
		refetchOnReconnect: false,
		// do not destructure args - accessing signal will cause strict mode to run the query twice.
		queryFn: async function validateAndMatch(args) {
			const [_, fileData, sheet, customMatchKeys, profile] = args.queryKey;

			if (!fileData || !sheet || !profile) return false;

			dispatch({ type: 'CLEAR_ERROR' });
			try {
				const rev = reverseMapping(mapRef);
				setImportState(ImportState.Validating);

				const rawData = fileData.workbook.GetSheet(sheet).extractData(profile);
				const formatted = rawData.map(r => {
					const m = mapValue(rev, r, mapTypes);
					const auto: Record<string, string> = {};
					for (const k in rev) auto[k] = m(k);
					return format(auto, r, rev, customMatchKeys);
				});

				const data: Array<ValidationResult<TImport>> = [];
				const matchOption = matchOptions?.find(o => o.id === algo);
				let chunkIndex = 0;
				if (!mounted.current) {
					return false;
				}
				await processArrayInChunks(formatted, chunkSize, async chunk => {
					if (!mounted.current) {
						return;
					}
					const result = await performValidateChunk(chunk, matchOption, chunkIndex, chunkSize, customMatchKeys);
					if (result.length > 0) {
						data.push(...result);
					}
					setPercent(Math.floor((data.length / formatted.length) * 100));
					chunkIndex++;
				});
				// don't dispatch in ths case
				if (!mounted.current) {
					return false;
				}

				dispatch({ type: 'PARSED', data });
			} catch (error) {
				dispatch({ type: 'ERROR', error });
			}
			setImportState(ImportState.Idle);

			return true;
		},
		queryKey
	});

	if (importState === ImportState.Validating || !state.data)
		return (
			<div style={{ height: '100%', display: 'grid', placeContent: 'center' }}>
				<FlexColumnCentered>
					<LoadingFox loading={true} height={'5rem'} />
				</FlexColumnCentered>
				<FlexColumnCentered>
					<Typography variant="body1">{percent}% validated</Typography>
				</FlexColumnCentered>
			</div>
		);

	if (state.error) {
		if (typeof state.error === 'string') {
			return <MuiAlert severity="error">{state.error}</MuiAlert>;
		} else if (state.error instanceof Error) {
			return <MuiAlert severity="error">{state.error.message}</MuiAlert>;
		} else {
			console.error(`Error is: ${state.error}`);
		}
	}

	return (
		<LargeStepWrapper>
			<FlexColumnSpaced>
				<ReviewComponent data={state.data} mappedColumns={mappedColumns} />
			</FlexColumnSpaced>
		</LargeStepWrapper>
	);
}
